import { useState, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useInstructionContent } from '@/hooks/api/useInstructionContent';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { cn } from '@/lib/utils';
import { InstructionRenderedBody } from '@/pages/organization/lib/formatInstructionContent';
import { TextHighlight } from '@/components/shared/TextHighlight';

interface InstructionDetailModalProps {
  organizationId: string;
  contentId: string | null;
  onClose: () => void;
  searchTerm?: string;
}

type ViewMode = 'raw' | 'rendered';

export function InstructionDetailModal({
  organizationId,
  contentId,
  onClose,
  searchTerm = '',
}: InstructionDetailModalProps): ReactElement {
  const { data, isLoading, error } = useInstructionContent(
    organizationId,
    contentId
  );
  const [viewMode, setViewMode] = useState<ViewMode>('raw');

  const hasToolbar = !isLoading && !error && Boolean(data);

  return (
    <Dialog open={contentId !== null} onOpenChange={() => onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <div className="flex flex-col gap-4 border-b p-6 pb-4">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="line-clamp-2 break-words leading-relaxed">
              {isLoading ? 'Loading...' : data?.generatedName || '(Untitled)'}
            </DialogTitle>
            {data?.generatedSummary && (
              <p className="text-sm text-muted-foreground">
                {data.generatedSummary}
              </p>
            )}
          </DialogHeader>

          {hasToolbar && data && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <div
                className="inline-flex shrink-0 rounded-md border border-border bg-muted/40 p-0.5"
                role="group"
                aria-label="Content view mode"
              >
                <button
                  type="button"
                  onClick={() => setViewMode('raw')}
                  className={cn(
                    'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                    viewMode === 'raw'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Raw
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('rendered')}
                  className={cn(
                    'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                    viewMode === 'rendered'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  Rendered
                </button>
              </div>
              <CopyToClipboardButton
                text={data.content}
                compactCopiedState
                size="sm"
                variant="outline"
                className="shrink-0"
              >
                <span className="sr-only">Copy to clipboard</span>
              </CopyToClipboardButton>
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading content...
            </div>
          ) : error ? (
            <div className="py-8 text-center text-destructive">
              Failed to load content
            </div>
          ) : data ? (
            <div className="space-y-2 pt-2">
              {viewMode === 'raw' ? (
                <pre className="max-h-[min(50vh,420px)] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-4 text-sm">
                  <code>
                    <TextHighlight
                      text={data.content}
                      searchTerm={searchTerm}
                    />
                  </code>
                </pre>
              ) : (
                <div className="max-h-[min(50vh,420px)] overflow-auto rounded-md border bg-muted/30 p-4 text-sm">
                  <InstructionRenderedBody content={data.content} />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
